import {Trans} from '@lingui/macro';
import React, {FC, useRef} from 'react';

import {VirtualTableHandle} from '../../../../../../../src/components/common/table/virtualTable';
import {globalNamespace} from '../../../../../../../src/helpers/models/namespaceHelpers';
import {AccountModel} from '../../../../../../../src/models/api/accountModel';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {Handler} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {AddContactButton} from '../common/buttons/addContactButton';
import {ContactsManagerHeader} from '../contactsManagerHeader';
import {accountDetailsContactsDropdownRenderTooltip} from '../helpers/contactsDropdownHelpers';
import {AccountContactsTableContainer} from '../tables/accountContacts/accountContactsTableContainer';
import {ContactsTableMultiSelectActions} from '../tables/common/contacts/contactsTableMultiSelectActions';

/*
 * Props.
 */

export interface AccountContactsPageProps {
  panelId: ContactsManagerPanelId;
  account?: AccountModel;
  selectedCardIds: ReadonlyArray<number>;
  navigateBackToAccounts: Handler;
  addCardsToAccount: (accountId: number, cardIds: ReadonlyArray<number>) => Promise<void>;
}

/*
 * Component.
 */

export const AccountContactsPage: FC<AccountContactsPageProps> = (props) => {
  const {panelId, account, selectedCardIds, navigateBackToAccounts, addCardsToAccount} = props;
  const tableRef = useRef<VirtualTableHandle>();

  const addCardToAccount = async (cardId: number) => {
    if (!account) {
      return;
    }
    await addCardsToAccount(account.id, [cardId]);
    if (tableRef && tableRef.current) {
      tableRef.current.forceReload();
    }
  };

  const renderAddContactButton = () => (
    <AddContactButton
      computeIsContactDisabled={(card) => Boolean(card.accountId)}
      renderTooltip={accountDetailsContactsDropdownRenderTooltip}
      filterSearchResults={(card) => card.namespace === globalNamespace}
      onConfirm={async (card, popoverProps) => {
        popoverProps?.onRequestClose();
        await addCardToAccount(card.id);
      }}
    />
  );

  return (
    <>
      <ContactsManagerHeader
        title={<Trans id="contacts-manager-account-contacts-title">Contacts for {account?.name}</Trans>}
        interactionId="contacts-manager-account-contacts-header"
        topLevelBreadcrumb={{
          label: <Trans id="contacts-manager-account-contacts-accounts-breadcrumb">Accounts</Trans>,
          onBreadcrumbClick: () => {
            navigateBackToAccounts();
          },
        }}
        actions={renderAddContactButton()}
        isLoading={!account}
      />
      {account && (
        <AccountContactsTableContainer
          panelId={panelId}
          account={account}
          tableRef={tableRef}
          multiSelectHeaderActions={
            <ContactsTableMultiSelectActions
              cardIds={selectedCardIds}
              shouldHideMergeAction
              shouldHideDeleteAction
            />
          }
          addCardToAccount={addCardToAccount}
        />
      )}
    </>
  );
};
